/**
 * Fetch an authenticated resource and hand it to the browser as a `blob:` object URL.
 *
 * WHY THIS EXISTS (H7, 2026-08-03). File viewers used to build URLs like
 *     `${API_BASE}/api/meal-plans/12/download-file?token=${localStorage.getItem('token')}`
 * and drop them straight into an <iframe src>. That puts a LIVE SESSION CREDENTIAL in a URL, which
 * means it lands in server access logs, browser history, and any Referer header — and a leaked URL
 * keeps working until the token expires, bypassing session revocation.
 *
 * A blob: URL removes the problem rather than shortening it: the bytes are fetched with an
 * Authorization header and handed to the browser in memory, so there is no URL to leak at all.
 * This is strictly stronger than the short-TTL signed-URL approach originally sketched for H7,
 * and it needs no new backend signing infrastructure.
 *
 * Proven pattern — `RecordsPage.jsx` (medical-record viewer) has used exactly this since the
 * medical-records `/view` endpoint was removed on 2026-07-29.
 *
 * ⚠️ CALLER OWNS REVOCATION. A blob URL pins its bytes for the lifetime of the document unless
 * revoked. Every createObjectURL needs a matching revoke when the viewer closes — use
 * `revokeBlobUrl()` in the close handler. Forgetting this leaks memory silently, which is exactly
 * the kind of thing that never shows up until a long session.
 *
 * ⚠️ NOT for HTML that contains ROOT-RELATIVE URLs. A blob: document has an opaque origin, so
 * `<img src="/api/...">` and `<a href="/api/...">` inside the fetched HTML will NOT resolve. Only
 * use this for binary/file responses or self-contained HTML. (See worklog/h7_signed_url_design.md
 * §4 — the recipes view emits relative URLs and needs its HTML fixed before it can move.)
 */

/**
 * @param {(url: string, opts?: object) => Promise<Response>} fetchWithAuth
 * @param {string} url  API URL WITHOUT any ?token= parameter
 * @returns {Promise<string>} a blob: object URL — caller must revoke it
 */
export async function authedBlobUrl(fetchWithAuth, url) {
  const res = await fetchWithAuth(url);
  if (!res.ok) throw new Error(`authedBlobUrl: ${res.status} for ${url}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/**
 * Release a blob URL. Safe to call with null/undefined or a non-blob string, so close handlers can
 * call it unconditionally without guarding.
 */
export function revokeBlobUrl(url) {
  if (typeof url === 'string' && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

/**
 * Download an authenticated file to the user's disk.
 *
 * Same reason as authedBlobUrl: the bytes need an Authorization header, so an <a href="/api/...">
 * cannot fetch them — it would send an unauthenticated request. Fetch, wrap in a blob, click a
 * synthetic anchor, revoke immediately (the download has already been handed to the browser).
 *
 * @param {(url: string, opts?: object) => Promise<Response>} fetchWithAuth
 * @param {string} url       API URL, no token parameter
 * @param {string} filename  suggested name for the saved file
 */
export async function downloadAuthedFile(fetchWithAuth, url, filename) {
  const res = await fetchWithAuth(url);
  if (!res.ok) throw new Error(`downloadAuthedFile: ${res.status} for ${url}`);
  const objectUrl = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename || 'download';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

export default authedBlobUrl;
